/** Evidence attached to a task: thumbnails for the approver, an upload for the doer.
 *
 *  These are the first images in this app, and the reason they need a component rather
 *  than an `<img src>` is documented in `api/client.ts`: the access token lives in
 *  memory, so a plain src attribute arrives unauthenticated and 401s. Every file is
 *  fetched with the auth header and wrapped in an object URL — which **must** be
 *  revoked, or the blob is held for the life of the tab. That is what the cleanups
 *  below are for, and it is the whole reason `Thumb` is its own component: one object
 *  URL per mounted thumbnail, revoked when that thumbnail unmounts.
 */
import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, objectUrl, upload, type Task } from '@/api/client'
import { ErrorNote } from './ui'

export type Attachment = {
  attachment_id: string
  filename: string
  mime: string
  bytes: number
  caption: string
  source: string
  uploaded_by: string
  created_at: string
  is_image: boolean
}

const fmtSize = (n: number) =>
  n >= 1024 * 1024 ? `${(n / (1024 * 1024)).toFixed(1)} MB`
                   : `${Math.max(1, Math.round(n / 1024))} KB`

function useAttachments(taskId: string) {
  return useQuery({
    queryKey: ['attachments', taskId],
    queryFn: () => api.get<Attachment[]>(`/tasks/${taskId}/attachments`),
    // A 404 here means "not yours to see", which the endpoint returns deliberately
    // rather than confirming the task carries evidence. Retrying would not change it.
    retry: false,
  })
}

/** One image, fetched authenticated and held as an object URL for its own lifetime. */
function Thumb({ taskId, a, onOpen }: {
  taskId: string; a: Attachment; onOpen: () => void
}) {
  const [url, setUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let alive = true
    let made: string | null = null
    objectUrl(`/tasks/${taskId}/attachments/${a.attachment_id}`)
      .then((u) => {
        made = u
        if (alive) setUrl(u)
        else URL.revokeObjectURL(u)   // unmounted while the fetch was in flight
      })
      .catch(() => { if (alive) setFailed(true) })
    return () => {
      alive = false
      if (made) URL.revokeObjectURL(made)
    }
  }, [taskId, a.attachment_id])

  if (failed) {
    return (
      <div className="h-16 w-16 shrink-0 grid place-items-center rounded border
                      border-rule bg-paper text-[10px] text-ink-3 text-center px-1">
        can&rsquo;t load
      </div>
    )
  }
  return (
    <button type="button" onClick={onOpen} title={a.filename}
            className="h-16 w-16 shrink-0 overflow-hidden rounded border border-rule
                       bg-paper hover:border-primary">
      {url ? (
        <img src={url} alt={a.caption || a.filename}
             className="h-full w-full object-cover" />
      ) : null}
    </button>
  )
}

/** Click-to-enlarge. Images inline, PDFs in an iframe — both from an object URL, so
 *  the same auth applies and neither leaves this origin. */
function Viewer({ taskId, a, onClose }: {
  taskId: string; a: Attachment; onClose: () => void
}) {
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    let made: string | null = null
    objectUrl(`/tasks/${taskId}/attachments/${a.attachment_id}`).then((u) => {
      made = u
      if (alive) setUrl(u); else URL.revokeObjectURL(u)
    }).catch(() => { /* the strip already showed it could not load */ })
    return () => { alive = false; if (made) URL.revokeObjectURL(made) }
  }, [taskId, a.attachment_id])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 bg-ink/70 p-3 sm:p-8 flex flex-col"
         onClick={onClose}>
      <div className="flex items-center gap-3 text-white text-[12.5px] pb-2">
        <b className="truncate">{a.filename}</b>
        <span className="mono opacity-70">{fmtSize(a.bytes)}</span>
        <span className="flex-1" />
        <button type="button" className="px-2 text-lg" aria-label="Close">×</button>
      </div>
      <div className="flex-1 min-h-0 grid place-items-center"
           onClick={(e) => e.stopPropagation()}>
        {!url ? <span className="text-white/70 text-xs">Loading…</span>
          : a.is_image
            ? <img src={url} alt={a.caption || a.filename}
                   className="max-h-full max-w-full object-contain" />
            : <iframe src={url} title={a.filename}
                      className="h-full w-full bg-white rounded" />}
      </div>
      {a.caption ? (
        <p className="text-white/80 text-[12px] pt-2 text-center">{a.caption}</p>
      ) : null}
    </div>
  )
}

/** The strip an approver reads. Renders nothing at all when there is nothing to show,
 *  so it can sit unconditionally in a queue row without adding empty furniture. */
export function AttachmentStrip({ task, label = 'Attached' }: {
  task: Task; label?: string
}) {
  const q = useAttachments(task.task_id)
  const [open, setOpen] = useState<Attachment | null>(null)
  const rows = q.data ?? []
  if (!rows.length) return null

  return (
    <>
      <div className="mt-2">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-3
                        mb-1.5">
          {label} — {rows.length}
        </div>
        <div className="flex gap-2 flex-wrap">
          {rows.map((a) => (a.is_image ? (
            <Thumb key={a.attachment_id} taskId={task.task_id} a={a}
                   onOpen={() => setOpen(a)} />
          ) : (
            <button key={a.attachment_id} type="button" onClick={() => setOpen(a)}
                    title={a.filename}
                    className="h-16 shrink-0 rounded border border-rule bg-paper px-2.5
                               text-left hover:border-primary max-w-[160px]">
              <span className="block text-[11px] font-semibold truncate">
                {a.filename}
              </span>
              <span className="mono block text-[10px] text-ink-3">
                {fmtSize(a.bytes)}
              </span>
            </button>
          )))}
        </div>
      </div>
      {open && <Viewer taskId={task.task_id} a={open}
                       onClose={() => setOpen(null)} />}
    </>
  )
}

/** The strip plus an upload control, for the task drawer. */
export function AttachmentPanel({ task, canAttach }: {
  task: Task; canAttach: boolean
}) {
  const qc = useQueryClient()
  const q = useAttachments(task.task_id)
  const input = useRef<HTMLInputElement>(null)
  const [err, setErr] = useState<unknown>(null)

  const add = useMutation({
    mutationFn: (file: File) =>
      upload<Attachment>(`/tasks/${task.task_id}/attachments`, file),
    onSuccess: () => {
      setErr(null)
      qc.invalidateQueries({ queryKey: ['attachments', task.task_id] })
    },
    onError: setErr,
  })

  // A 404 is "not yours to see" and is not worth a red box on somebody's screen.
  if (q.error) return null

  return (
    <div className="px-5 py-3 border-b border-rule">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-3">
          Attachments — {q.data?.length ?? 0}
        </span>
        <span className="flex-1" />
        {canAttach && (
          <>
            <input ref={input} type="file" className="hidden"
                   accept="image/*,application/pdf"
                   onChange={(e) => {
                     const f = e.target.files?.[0]
                     // Cleared so choosing the same file twice fires onChange again.
                     e.target.value = ''
                     if (f) add.mutate(f)
                   }} />
            <button type="button" className="btn btn-sm"
                    disabled={add.isPending}
                    onClick={() => input.current?.click()}>
              {add.isPending ? 'Adding…' : 'Add a file'}
            </button>
          </>
        )}
      </div>
      {err ? <div className="mt-2"><ErrorNote error={err} /></div> : null}
      <AttachmentStrip task={task} label="On this task" />
      {!q.data?.length ? (
        <p className="text-[11.5px] text-ink-3 mt-1">
          {canAttach
            ? 'A photo or a PDF — whoever approves this can look at it before deciding. You can also reply to the bot with a photo.'
            : 'Nothing attached.'}
        </p>
      ) : null}
    </div>
  )
}
